import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import InfoIcon from "@mui/icons-material/Info";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
  type GridColDef,
  type GridFilterModel,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import DOMPurify from "dompurify";
import { isEqual } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PatientContext } from "../../../contexts/PatientContext";
import { ProjectContext } from "../../../contexts/ProjectContext";
import { projectsPrefix } from "../../../services/ProjectsServices.ts";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { convertDate } from "../../../utils/dateTimeUtils.ts";
import { getDiffDaysMessage } from "../../../utils/main.ts";
import BaseTable from "../../datagrid/BaseTable.tsx";
import { BaseTableToolbar } from "../../datagrid/BaseTableToolbar.tsx";
import { TableNoRowsCustomization } from "../../datagrid/TableNoRowsCustomization.tsx";
import DocumentModal from "./DocumentModal.tsx";
import { filters, remapNoteFilters } from "./filters/ESDocFilters.ts";

const CustomNoRowsOverlay = () => {
  return (
    <TableNoRowsCustomization
      icon={
        <CloudUploadOutlined
          style={{
            color: "#0D3E56",
            width: "48px",
            height: "48px",
            marginLeft: "18px",
          }}
        />
      }
      text={"This visit does not include any Order-Level Documents."}
    />
  );
};

export default function OrderLevelDocumentsCard({
  categorize,
}: {
  categorize: (e?: React.MouseEvent<HTMLElement>) => void;
}) {
  const { id: projectId, highlightDate } = useContext(ProjectContext) || {};
  const theme = useTheme();
  const { id, orderLevelDocumentCount: documentCounts } =
    useContext(PatientContext) || {};
  const [documentModal, setDocumentModal] = useState(false);
  const [document, setDocument] = useState<ESDoc | null>(null);
  const [filteredRowIds, setFilteredRowIds] = useState<string[]>([]);
  const {
    data: documentsData,
    create: fetchDocuments,
    isLoading: isDocumentsLoading,
    error: fetchDocumentsError,
  } = useFetch<PagedResponse<ESDoc>, FilterPageSortRequest>();

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 100,
    page: 0,
  });

  const [filter, setFilter] = useState<GridFilterModel>();

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [previouslyUsedFetchParams, setPreviouslyUsedFetchParams] =
    useState<FilterPageSortRequest | null>(null);

  const [viewStateReady, setViewStateReady] = useState(false);
  const [requireDocumentsFetch, setRequireDocumentsFetch] = useState(true);

  const orderLevelDocsDataTableFields: GridColDef<ESDoc>[] = [
    {
      field: "noteTitle",
      headerName: "Title",
      width: 100,
      filterOperators: filters.noteTitle,
    },
    {
      field: "diffDays",
      headerName: "Pre / Post Index",
      width: 100,
      valueGetter: (value) => getDiffDaysMessage(value),
      filterOperators: filters.diffDays,
    },
    {
      field: "notePreview",
      headerName: "Content",
      flex: 1,
      minWidth: 175,
      renderCell: (params) => {
        const processedText = params.row.notePreview.replaceAll("\n", "<br />");

        const shortenedText =
          processedText.length >= 100 ? `${processedText}...` : processedText;

        const finalText = DOMPurify.sanitize(shortenedText, {
          ALLOWED_TAGS: ["highlight"],
          RETURN_TRUSTED_TYPE: true,
        });

        return (
          <Link
            sx={{ textAlign: "left" }}
            component="button"
            variant="subtitle2"
            onClick={() => {
              handleDocumentModal(params.row);
            }}
            dangerouslySetInnerHTML={{ __html: finalText }}
          />
        );
      },
      filterOperators: filters.notePreview,
    },
    {
      field: "keywordScore",
      headerName: "Priority",
      renderHeader: () => (
        <div className="MuiDataGrid-columnHeaderTitle">
          <Tooltip
            title="Visits with a priority icon may include evidence of an adverse event, clinical or performance failure as identified by the 3Aware system."
            placement="top-end"
            className="priority"
          >
            <p>
              Priority
              <InfoIcon
                fontSize="small"
                sx={{ color: theme.palette.info.main }}
              />
            </p>
          </Tooltip>
        </div>
      ),
      renderCell: (params) => {
        return (
          params.row.keywordScore > 0 && (
            <OfflineBoltOutlinedIcon sx={{ color: theme.palette.info.main }} />
          )
        );
      },
      align: "center",
      width: 85,
      filterOperators: filters.keywordScore,
    },
  ];

  function closeDocumentModal() {
    setDocumentModal(false);
  }

  function handleDocumentModal(document: ESDoc) {
    setDocumentModal(true);
    setDocument(document);
  }

  const apiRef = useGridApiRef();

  if (apiRef.current) {
    const currentFilteredSortedRowIds =
      gridFilteredSortedRowIdsSelector(apiRef);
    if (!isEqual(currentFilteredSortedRowIds, filteredRowIds)) {
      setFilteredRowIds(currentFilteredSortedRowIds.map(String));
    }
  }

  const documentIndex = useMemo(() => {
    if (!filteredRowIds || !document || filteredRowIds.length === 0) {
      return -1;
    }
    return filteredRowIds.findIndex((docId) => docId === document?.documentId);
  }, [document, filteredRowIds]);

  function goPrevDocument() {
    if (filteredRowIds && filteredRowIds.length > 0 && documentIndex > 0) {
      const prevDocumentId = filteredRowIds[documentIndex - 1];
      setDocument(
        documentsData?.results?.find(
          (doc) => doc.documentId === prevDocumentId,
        ) || null,
      );
    }
  }

  function goNextDocument() {
    if (
      filteredRowIds &&
      filteredRowIds.length > 0 &&
      documentIndex < filteredRowIds.length - 1
    ) {
      const nextDocumentId = filteredRowIds[documentIndex + 1];
      setDocument(
        documentsData?.results?.find(
          (doc) => doc.documentId === nextDocumentId,
        ) || null,
      );
    }
  }

  useEffect(() => {
    if (fetchDocumentsError) {
      console.log("Error: " + fetchDocumentsError);
    }
  }, [fetchDocumentsError]);

  const getDocuments = useCallback(() => {
    const localFilterItems = filter?.items
      ? remapNoteFilters(filter?.items)
      : [];
    const fetchBody = {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: [...sortModel],
      search: filter?.quickFilterValues?.join(" ").trim() || "",
      filters: localFilterItems,
      filterLogicalOperator: filter?.logicOperator?.toUpperCase(),
    };

    // fetch all order-level docs w/ preview only for patient
    if (!isEqual(fetchBody, previouslyUsedFetchParams)) {
      setPreviouslyUsedFetchParams(fetchBody);
      void fetchDocuments({
        path: `${projectsPrefix}/${projectId}/patients/${id}/documents`,
        body: fetchBody,
        responseManipulationFunction: (data: PagedResponse<ESDoc>) => {
          if (
            data === null ||
            data.results === null ||
            data.results?.length === 0
          ) {
            return {
              pageNumber: paginationModel.page,
              pageSize: paginationModel.pageSize,
              offset: paginationModel.page * paginationModel.pageSize,
              rowCount: 0,
              results: [],
            };
          } else {
            // show order-level docs only if patient has at least 1 returned
            data.results.map(
              (doc) =>
                (doc.highlighted =
                  highlightDate &&
                  convertDate.dateIsAfterHighlight(
                    highlightDate,
                    doc.loadedAt,
                  )),
            );
            return data;
          }
        },
      });
    }
  }, [
    fetchDocuments,
    id,
    projectId,
    highlightDate,
    paginationModel.page,
    paginationModel.pageSize,
    filter?.items,
    filter?.logicOperator,
    filter?.quickFilterValues,
    sortModel,
    previouslyUsedFetchParams,
  ]);

  useEffect(() => {
    if (requireDocumentsFetch && viewStateReady) {
      getDocuments();
      setRequireDocumentsFetch(false);
    }
  }, [requireDocumentsFetch, getDocuments, viewStateReady]);

  return (
    <Card className="visit-drawer-card order-level-card">
      <CardContent>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" className="section-header-title">
            Order-Level Documents/PDFs
          </Typography>
          {documentsData && !isDocumentsLoading ? (
            <Chip
              label={documentCounts?.total ?? 0}
              size="small"
              sx={{
                marginLeft: "6px",
              }}
            />
          ) : (
            <CircularProgress size={20} />
          )}
          {!!highlightDate && !!documentsData && (
            <Chip
              label={(documentCounts?.highlight ?? 0) + " new"}
              size="small"
              sx={{
                marginLeft: "6px",
                backgroundColor: "highlight.main",
                textTransform: "none",
              }}
            />
          )}
        </Stack>
        <BaseTable<ESDoc>
          apiRef={apiRef}
          tableId={"order-level-documents-table"}
          columns={orderLevelDocsDataTableFields}
          rows={documentsData?.results || []}
          getRowId={(row) => row.documentId}
          highlightField={"loadedAt"}
          columnHeaderHeight={55}
          loading={isDocumentsLoading}
          className={"note-table"}
          tableHeight={"auto"}
          maxTableHeight={450}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: BaseTableToolbar,
          }}
          slotProps={{
            toolbar: {
              onViewStateReady: () => setViewStateReady(true),
            },
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
            setRequireDocumentsFetch(true);
          }}
          rowCount={documentsData?.rowCount ?? 0}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setRequireDocumentsFetch(true);
          }}
          filterMode="server"
          filterModel={filter}
          onFilterModelChange={(filterModel) => {
            setFilter(filterModel);
            setRequireDocumentsFetch(true);
          }}
          columnVisibilityModel={{ noteText: false }}
          filterDebounceMs={600}
          sx={{ "--DataGrid-overlayHeight": "150px" }}
        />
        {documentModal && document && (
          <DocumentModal
            document={document}
            categorize={categorize}
            closeModal={closeDocumentModal}
            isFirst={documentIndex === 0}
            isLast={
              !!filteredRowIds &&
              filteredRowIds.length > 0 &&
              documentIndex === filteredRowIds.length - 1
            }
            goPrevDocument={goPrevDocument}
            goNextDocument={goNextDocument}
          />
        )}
      </CardContent>
    </Card>
  );
}
